import React, { createContext, useContext, useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  verifyBeforeUpdateEmail,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, googleProvider } from "./firebaseConfig";
import { apiFetch } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Subscribe to Firebase auth state once on mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoadingAuth(false);
    });
    return unsubscribe;
  }, []);

  async function register(email, password, displayName) {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(credential.user, { displayName });
    }
    // The confirm-your-address mail is sent by OUR backend, not by Firebase:
    // Firebase's goes to a page it hosts that is not translated, and its
    // delivery is invisible to us — a player wrote on 2026-08-14 that it never
    // arrived and there was nothing we could check. Best-effort: a mail that
    // does not go out must never fail the registration itself.
    try {
      const token = await credential.user.getIdToken();
      await apiFetch("/auth/send_verification", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* the account exists either way; the button in settings resends */ }
    return credential;
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function loginWithGoogle() {
    return signInWithPopup(auth, googleProvider);
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // verifyBeforeUpdateEmail, not updateEmail — the difference is the whole point.
  //
  // updateEmail writes the new address immediately and Firebase refuses to do it
  // while the CURRENT address is unverified. That locks out exactly the person
  // who needs it most: somebody who mistyped their address at registration can
  // never verify it (it isn't theirs, or doesn't exist), and therefore can never
  // correct it either. Reported 2026-08-27 by a player who registered .cx
  // instead of .cz and wrote in because the profile would not let him fix it —
  // "což logicky nedokážu".
  //
  // verifyBeforeUpdateEmail sends the confirmation to the NEW address and swaps
  // it only once that link is clicked. It never asks about the old one, so the
  // deadlock cannot happen. It is also the API Firebase now points at; updateEmail
  // is deprecated.
  //
  // It sends the mail itself, so there is no sendEmailVerification here — calling
  // it as well would send a second mail to the OLD address, which is the one we
  // just established may not exist.
  //
  // NOTE: the address does not change when this resolves; it changes when the
  // player clicks. Anything telling them "done" has to say "check your inbox"
  // instead, or they will close the page believing it is finished.
  async function changeEmail(newEmail) {
    await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
  }

  // Returns a fresh ID token to send as Authorization: Bearer <token>
  // forceRefresh=true ensures custom claims (e.g. test_account) are included
  async function getIdToken(forceRefresh = false) {
    if (!currentUser) return null;
    return currentUser.getIdToken(forceRefresh);
  }

  // Returns custom claims from the ID token (includes test_account flag)
  async function getClaims() {
    if (!currentUser) return {};
    // Force refresh to pick up latest custom claims
    const result = await currentUser.getIdTokenResult(true);
    return result.claims || {};
  }

  const value = {
    currentUser,
    loadingAuth,
    register,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    changeEmail,
    getIdToken,
    getClaims,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
